import type { StateCreator, StoreApi, UseBoundStore } from 'zustand';

import { PaymentMethodsEnum } from '@ctct/domain-layer';
import type { AmazonPayCheckoutSessionResponse } from '@ctct/domain-layer';
import type { ApplePayCreditCardResponse } from '@ctct/infrastructure-layer';

export type PaymentMethodStripeState = { type: 'editable' } | { type: 'confirmed'; paymentMethodId: string | null };

export enum PaymentMethodStripeEditState {
  Editable = 'editable',
  SavedPaymentMethod = 'saved-payment-method',
  NewPaymentMethodNonEditable = 'new-payment-method-non-editable',
}

export interface IPaymentMethodsSlice {
  selectedMethod: PaymentMethodsEnum;
  currentPaymentMethod: PaymentMethodsEnum | null;

  /**
   * `true` if a form is being displayed for the user to add/edit a payment method;
   * `false` if we are displaying a saved payment method.
   */
  paymentMethodCanBeUpdated: boolean;
  paymentMethodStripeEditState: PaymentMethodStripeEditState;
  tabsLoading: boolean;
  paymentMethodsFullLoading: boolean;
  paymentMethodsExternalRedirectLoading: boolean;
  paymentMethodsUpdatingStripeLocaleConfig: boolean;
  currentPaymentMethodWarning: null | string;

  showSecurePayIframeBlockedMessage: boolean;
  showCvvTooltip: boolean;
  addedCssToIframe: boolean;
  creditCardToken: string | null;
  creditCardConnected: boolean;
  echeckConnected: boolean;
  securePayEdited: boolean;
  echeckToken: string | null;
  paypalAgreementId: string | null;
  paypalConnected: boolean;
  amazonPayToken: string | null;
  amazonPayCheckoutSession: AmazonPayCheckoutSessionResponse | null;
  amazonPayConnected: boolean;

  // stripe
  isStripeSubmitted: boolean;
  paymentMethodStripeState: PaymentMethodStripeState;

  // apple pay
  applePayConnected: boolean;
  applePayCreditCardData: null | ApplePayCreditCardResponse['data']['creditCard'];
  applePayToken: string | null;
  applePayTotal: null | {
    amount: number;
    taxAmount: number;
    currency: string;
  };
  applePayTokenExpired: boolean;
  applePayTotalChanged: boolean;
  applePayZeroValuePayment: null | {
    currency: string;
  };
  applePaySecurePayIframeBlockedMessage: boolean;
  applePayAddedCssToIframe: boolean;
  applePayShouldGetAddressWhenLoaded: boolean;
  applePayShouldGetTaxUpdateWhenLoaded: boolean;
}

export const createPaymentMethodsSliceDefaultValues = (): IPaymentMethodsSlice => ({
  selectedMethod: PaymentMethodsEnum.CREDIT_CARD,
  currentPaymentMethod: null,
  paymentMethodCanBeUpdated: false,
  paymentMethodStripeEditState: PaymentMethodStripeEditState.Editable,
  tabsLoading: false,
  paymentMethodsFullLoading: false,
  paymentMethodsExternalRedirectLoading: false,
  paymentMethodsUpdatingStripeLocaleConfig: false,
  currentPaymentMethodWarning: null,

  showSecurePayIframeBlockedMessage: false,
  showCvvTooltip: false,
  addedCssToIframe: false,
  creditCardToken: null,
  creditCardConnected: false,
  echeckConnected: false,
  securePayEdited: false,
  echeckToken: null,
  paypalAgreementId: null,
  paypalConnected: false,
  amazonPayToken: null,
  amazonPayCheckoutSession: null,
  amazonPayConnected: false,

  // stripe
  isStripeSubmitted: false,
  paymentMethodStripeState: { type: 'editable' },

  // apple pay
  applePayConnected: false,
  applePayCreditCardData: null,
  applePayToken: null,
  applePayTotal: null,
  applePayTokenExpired: false,
  applePayTotalChanged: false,
  applePayZeroValuePayment: null,
  applePaySecurePayIframeBlockedMessage: false,
  applePayAddedCssToIframe: false,
  applePayShouldGetAddressWhenLoaded: false,
  applePayShouldGetTaxUpdateWhenLoaded: false,
});

export type PaymentMethodsPageStore = UseBoundStore<StoreApi<IPaymentMethodsSlice>>;

export const createPaymentMethodsSlice: StateCreator<IPaymentMethodsSlice> = () =>
  createPaymentMethodsSliceDefaultValues();

export const createPaymentMethodsHooks = (pageStore: PaymentMethodsPageStore) => {
  return {
    useSelectedMethod: () => pageStore((state) => state.selectedMethod),
    useSecurePayIframeState: () =>
      pageStore((state) => ({
        showCvvTooltip: state.showCvvTooltip,
        addedCssToIframe: state.addedCssToIframe,
        creditCardConnected: state.creditCardConnected,
        echeckConnected: state.echeckConnected,
        securePayEdited: state.securePayEdited,
        showSecurePayIframeBlockedMessage: state.showSecurePayIframeBlockedMessage,
      })),
    useApplePaySecurePayIframeState: () =>
      pageStore((state) => ({
        applePayAddedCssToIframe: state.applePayAddedCssToIframe,
        applePayConnected: state.applePayConnected,
        applePaySecurePayIframeBlockedMessage: state.applePaySecurePayIframeBlockedMessage,
        applePayTokenExpired: state.applePayTokenExpired,
        applePayTotal: state.applePayTotal,
        applePayTotalChanged: state.applePayTotalChanged,
        applePayZeroValuePayment: state.applePayZeroValuePayment,
      })),
    usePaypalConnected: () => pageStore((state) => state.paypalConnected),
    useApplePayConnected: () => pageStore((state) => state.applePayConnected),
    useApplePayCreditCardData: () => pageStore((state) => state.applePayCreditCardData),
    useAmazonPayConnected: () => pageStore((state) => !!state.amazonPayCheckoutSession || state.amazonPayConnected),
    useTabsLoading: () => pageStore((state) => state.tabsLoading),
    useFullLoading: () => pageStore((state) => state.paymentMethodsFullLoading),
    useUpdatingStripeLocaleConfig: () => pageStore((state) => state.paymentMethodsUpdatingStripeLocaleConfig),
    useStripeSubmitted: () => pageStore((state) => state.isStripeSubmitted),
    useCurrentPaymentMethodWarning: () => pageStore((state) => state.currentPaymentMethodWarning),
    useHasPendingExternalRedirect: () => pageStore((state) => state.paymentMethodsExternalRedirectLoading),
    usePaymentMethodStripeEditState: () => pageStore((state) => state.paymentMethodStripeEditState),
  };
};

export type PaymentMethodsHooks = ReturnType<typeof createPaymentMethodsHooks>;
